import React, { Key, useCallback, useMemo, useState } from 'react';
import { SkuResponse } from '@ydistri/api-sdk';
import { YColumnsType } from '@ydistri/ds';
import { extendSkuResponse } from '../../../lib/sales/salesLib';
import { parseSkuAttributes } from '../../../lib/sku/skuLib';
import SKUAttributes from './SKUAttributes';
import SkuDetailsTable from '../SkuDetailsTable/SkuDetailsTable';
import NoData from '../../global/NoData/NoData';
import { compareStoreDecile } from './skuTableLib';

interface SKUTableProps {
  data: SkuResponse[];
  currency: string;
}

const SKUTableComponent: React.FC<SKUTableProps> = ({ data, currency }) => {
  //some attributes are moved to the top level of the object for easier access in the table (for filtering and sorting)
  const extendedData = useMemo(() => extendSkuResponse(data), [data]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  const customizeColumns = useCallback((columns: YColumnsType<SkuResponse>[]) => {
    const lastStoreColumnIndex = columns.findLastIndex(
      column => Array.isArray(column.dataIndex) && column.dataIndex[0] === 'store',
    );
    const insertIndex = lastStoreColumnIndex === -1 ? 0 : lastStoreColumnIndex + 1;

    const storeDecileColumn: YColumnsType<SkuResponse> = {
      title: 'Store Decile',
      dataIndex: ['store', 'storeDecile'],
      align: 'right',
      width: '5rem',
      render: (value?: number | null) => value?.toString() ?? <NoData />,
      sorter: (left, right, sortOrder) =>
        compareStoreDecile(left.store?.storeDecile, right.store?.storeDecile, sortOrder),
    };

    const modifiedColumns = [...columns];
    modifiedColumns.splice(insertIndex, 0, storeDecileColumn);
    return modifiedColumns;
  }, []);

  const expandableConfig = useMemo(() => {
    return {
      expandRowByClick: true,
      showExpandColumn: false,
      expandedRowRender: (record: SkuResponse) => {
        if (record.attributes) {
          const skuEntity = parseSkuAttributes(record.skuId, record.attributes);
          return <SKUAttributes sku={skuEntity} />;
        }
      },
      expandedRowKeys: expandedRowKeys,
      onExpandedRowsChange: (expandedRowKeys: readonly Key[]) => {
        setExpandedRowKeys([...expandedRowKeys]);
      },
    };
  }, [expandedRowKeys]);

  return (
    <SkuDetailsTable<SkuResponse>
      data={extendedData}
      expandable={expandableConfig}
      currency={currency}
      customizeColumns={customizeColumns}
    />
  );
};

export default SKUTableComponent;
